import {
    isValidElement,
    type ReactNode,
    Children
} from "react";
import type {
    MessageFormatTimeConstantType,
    MessageActionItemType,
    MessageBubbleInfoType,
    MessageFocusEventType
} from "./type";
import type {
    NCoreUIKitIcon
} from "../../types";
import type {
    BubbleVariant
} from "../bubble/type";
import {
    formatDateTime
} from "../../helpers/locale";
import Bubble from "../bubble";

type MessageBubblePropsType = {
    onReactionAdd?: (emoji: string) => void;
    reactionsSide?: "top" | "bottom";
    reactions?: Array<unknown>;
    variant?: BubbleVariant;
};

type MessageActionElementPropsType = {
    onPressedChange?: (isPressed: boolean) => void;
    accessibilityLabel?: string;
    isDefaultPressed?: boolean;
    "aria-label"?: string;
    icon?: NCoreUIKitIcon;
    onPress?: () => void;
    isPressed?: boolean;
    title?: string;
    type?: string;
};

const MILLISECONDS_PER_DAY = 86400000;

const getStartOfDay = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

export const getMessageBubbleInfo = (children: ReactNode): MessageBubbleInfoType => {
    const bubble = Children.toArray(children).find((child) => {
        return isValidElement<MessageBubblePropsType>(child) && child.type === Bubble;
    });

    if(!isValidElement<MessageBubblePropsType>(bubble)) {
        return {
            isHasReactions: false
        };
    }

    return {
        isHasReactions: Boolean(bubble.props.reactions?.length || bubble.props.onReactionAdd) && bubble.props.reactionsSide !== "top",
        variant: bubble.props.variant ?? "assistant"
    };
};

export const getMessageActionItems = (children: ReactNode): Array<MessageActionItemType> => {
    const items: Array<MessageActionItemType> = [];

    Children.toArray(children).forEach((child, index) => {
        if(!isValidElement<MessageActionElementPropsType>(child)) {
            return;
        }

        const {
            "aria-label": ariaLabel,
            accessibilityLabel,
            isDefaultPressed,
            onPressedChange,
            isPressed,
            onPress,
            title,
            icon,
            type
        } = child.props;

        const label = title ?? ariaLabel ?? accessibilityLabel;

        if(!label || (!onPress && !onPressedChange)) {
            return;
        }

        items.push({
            onPress: () => {
                if(onPress) {
                    onPress();

                    return;
                }

                onPressedChange?.(!(isPressed ?? isDefaultPressed ?? false));
            },
            key: child.key ?? `${index}`,
            isDanger: type === "danger",
            label,
            icon
        });
    });

    return items;
};

export const getMessageIsFocusWithin = ({
    relatedTarget,
    currentTarget
}: MessageFocusEventType): boolean => {
    if(typeof Node === "undefined" || !(currentTarget instanceof Node) || !(relatedTarget instanceof Node)) {
        return false;
    }

    return currentTarget.contains(relatedTarget);
};

export const getMessageIsFocusVisible = (target: unknown): boolean => {
    if(typeof target !== "object" || target === null || !("matches" in target) || typeof target.matches !== "function") {
        return true;
    }

    try {
        return target.matches(":focus-visible");
    } catch {
        return true;
    }
};

export const formatMessageTime = ({
    now = new Date(),
    timestamp,
    localize,
    locale
}: MessageFormatTimeConstantType) => {
    const time = formatDateTime({
        date: timestamp,
        preset: "time",
        locale
    });

    const dayDifference = Math.round((getStartOfDay(now) - getStartOfDay(timestamp)) / MILLISECONDS_PER_DAY);

    if(dayDifference <= 0) {
        return time;
    }

    if(dayDifference === 1) {
        return localize("yesterday-at-x", [
            time
        ]);
    }

    return formatDateTime({
        options: {
            month: "short",
            day: "numeric"
        },
        date: timestamp,
        locale
    });
};

export const formatMessageFullTime = ({
    timestamp,
    locale
}: {
    timestamp: Date;
    locale: string;
}) => {
    return formatDateTime({
        options: {
            minute: "2-digit",
            hour: "2-digit",
            weekday: "long",
            year: "numeric",
            month: "short",
            day: "numeric"
        },
        date: timestamp,
        locale
    });
};
